import React, { useState, useEffect } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import { useUpdateRewardSettingsMutation, useLazyGetReferralLevelsQuery } from "../../redux/rewardSettingsApi";
import { useGetCryptocurrencyQuery } from "../../redux/cryptocurrencyApi";

import { toast } from "react-toastify";

import { useTranslation } from "react-i18next";

function EditRewardSettings({ show, onHide, rewardSettings }) {
    const { t } = useTranslation();
    const [editData, setEditData] = useState({});
    const [updateRewardSettings] = useUpdateRewardSettingsMutation();
    const [getReferralLevels] = useLazyGetReferralLevelsQuery();
    const { data: cryptoResponse } = useGetCryptocurrencyQuery();

    const cryptocurrencies = cryptoResponse?.data || [];

    useEffect(() => {
        const fetchLevelsAndSet = async () => {
            if (rewardSettings && rewardSettings.id) {
                let levels = rewardSettings.referral_levels || [];
                // If referral levels are not already populated on the object, fetch them
                if (levels.length === 0) {
                    try {
                        const response = await getReferralLevels(rewardSettings.id).unwrap();
                        if (response.status) {
                            levels = response.data || [];
                        }
                    } catch (error) {
                        console.error("Error fetching referral levels for edit:", error);
                    }
                }

                if (levels.length < 15) {
                    const existingLevelsMap = new Map(levels.map(l => [l.level, l]));
                    levels = Array.from({ length: 15 }, (_, i) => {
                        const lvl = i + 1;
                        return existingLevelsMap.get(lvl) || {
                            level: lvl,
                            reward_level_amount: "",
                            status: "enabled"
                        };
                    });
                }
                setEditData({
                    reward_type: "registration_bonus",
                    ...rewardSettings,
                    referral_levels: levels
                });
            }
        };

        if (show) {
            fetchLevelsAndSet();
        }
    }, [rewardSettings, show, getReferralLevels]);

    const handleEditSave = async () => {
        const payload = {
            ...editData,
            referral_levels: editData.reward_type === "referral_bonus" ? editData.referral_levels : []
        };
        try {
            const response = await updateRewardSettings(payload).unwrap();
            if (response.status) {
                toast.success(response.message || "Reward settings updated successfully!");
                onHide();
            } else {
                toast.error(response.message || "Failed to update reward settings");
            }
        } catch (error) {
            toast.error("Error updating reward settings");
            console.error("Error updating reward settings:", error);
        }
    };

    return (
        <Modal show={show} onHide={onHide}>
            <Modal.Header closeButton>
                <Modal.Title>{t('edit_reward_settings')}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Form>
                    <Form.Group className="mb-3">
                        <Form.Label>{t('cryptocurrency')}</Form.Label>
                        <Form.Select
                            value={editData.reward_coin_id || ""}
                            onChange={(e) => setEditData({ ...editData, reward_coin_id: e.target.value })}
                        >
                            <option value="">{t('select_cryptocurrency')}</option>
                            {cryptocurrencies.map((crypto, index) => (
                                <option key={index} value={crypto.id}>
                                    {crypto.coin_name} ({crypto.symbol})
                                </option>
                            ))}
                        </Form.Select>
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>{t('amount')}</Form.Label>
                        <Form.Control
                            type="number"
                            step="any"
                            value={editData.reward_amount || ""}
                            onChange={(e) => setEditData({ ...editData, reward_amount: e.target.value })}
                        />
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>{t('reward_type') || 'Reward Type'}</Form.Label>
                        <Form.Select
                            value={editData.reward_type || "registration_bonus"}
                            onChange={(e) => setEditData({ ...editData, reward_type: e.target.value })}
                        >
                            <option value="registration_bonus">{t('registration_bonus') || 'Registration Bonus'}</option>
                            <option value="referral_bonus">{t('referral_bonus') || 'Referral Bonus'}</option>
                        </Form.Select>
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>{t('status')}</Form.Label>
                        <Form.Select
                            value={editData.status || "enabled"}
                            onChange={(e) => setEditData({ ...editData, status: e.target.value })}
                        >
                            <option value="enabled">{t('enabled')}</option>
                            <option value="disabled">{t('disabled')}</option>
                        </Form.Select>
                    </Form.Group>

                    {editData.reward_type === "referral_bonus" && (
                        <>
                            <hr />
                            <h5>{t('referral_levels')}</h5>
                            <div style={{ maxHeight: '300px', overflowY: 'auto', paddingRight: '5px' }}>
                                {editData.referral_levels?.map((levelObj, index) => (
                                    <div key={index} className="d-flex mb-2 align-items-center">
                                        <Form.Label className="me-2 mb-0" style={{ minWidth: '80px' }}>
                                            {t('level')} {levelObj.level}
                                        </Form.Label>
                                        <Form.Control
                                            type="number"
                                            step="any"
                                            placeholder={t('reward_level_amount') || 'Reward Amount'}
                                            value={levelObj.reward_level_amount || ""}
                                            onChange={(e) => {
                                                const newLevels = [...(editData.referral_levels || [])];
                                                newLevels[index] = { ...newLevels[index], reward_level_amount: e.target.value };
                                                setEditData({ ...editData, referral_levels: newLevels });
                                            }}
                                            className="me-2"
                                        />
                                        <Form.Select
                                            value={levelObj.status || "enabled"}
                                            onChange={(e) => {
                                                const newLevels = [...(editData.referral_levels || [])];
                                                newLevels[index] = { ...newLevels[index], status: e.target.value };
                                                setEditData({ ...editData, referral_levels: newLevels });
                                            }}
                                        >
                                            <option value="enabled">{t('enabled')}</option>
                                            <option value="disabled">{t('disabled')}</option>
                                        </Form.Select>
                                    </div>
                                ))}
                            </div>
                        </>
                    )}
                </Form>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={onHide}>
                    {t('close')}
                </Button>
                <Button variant="primary" onClick={handleEditSave}>
                    {t('save_changes')}
                </Button>
            </Modal.Footer>
        </Modal>
    );
}

export default EditRewardSettings;
